import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../config";
import {
  Rocket,
  CheckCircle,
  AlertTriangle,
  GraduationCap,
  Zap,
  Award,
  Shield,
  Star,
} from "lucide-react";

export default function Dashboard() {
  const [salesData, setSalesData] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const cards = [
    {
      title: "Total Startups",
      key: "allStartups",
      plan: "all",
      icon: Rocket,
      color: "#435ebe",
      bg: "#eef2ff",
    },
    {
      title: "Active Startups",
      key: "activeCount",
      plan: "active",
      icon: CheckCircle,
      color: "#10b981",
      bg: "#ecfdf5",
    },
    {
      title: "Inactive Startups",
      key: "inactiveCount",
      plan: "inactive",
      icon: AlertTriangle,
      color: "#ef4444",
      bg: "#fef2f2",
    },
    {
      title: "University Startups",
      key: "uniCount",
      plan: "university",
      icon: GraduationCap,
      color: "#64748b",
      bg: "#f1f5f9",
    },
    {
      title: "Alpha Plan Startups",
      key: "alphaCount",
      plan: "alpha",
      icon: Zap,
      color: "#f59e0b",
      bg: "#fffbeb",
    },
    {
      title: "Beta Plan Startups",
      key: "betaCount",
      plan: "beta",
      icon: Award,
      color: "#8b5cf6",
      bg: "#faf5ff",
    },
    {
      title: "Gamma Plan Startups",
      key: "gammaCount",
      plan: "gamma",
      icon: Shield,
      color: "#06b6d4",
      bg: "#ecfeff",
    },
    {
      title: "Sigma Plan Startups",
      key: "sigmaCount",
      plan: "sigma",
      icon: Star,
      color: "#0f172a",
      bg: "#f8fafc",
    },
  ];

  const handleClick = (plan) => {
    navigate("/startups", { state: { plan } });
  };

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      try {
        const res = await apiClient.get("/api/admin_routes/reports", {
          signal: controller.signal,
        });

        if (res.data && res.data.data) {
          setSalesData(res.data.data);
        }
      } catch (err) {
        if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
          console.error("Error fetching dashboard data:", err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => controller.abort(); // Cleanup on unmount
  }, []);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 px-3">
        <div>
          <h4 className="fw-semibold mb-1">Administrative Dashboard</h4>
          <p className="text-muted mb-0 small">Overview of active startup plans, verification pipelines, and platform stats</p>
        </div>
      </div>

      <div className="row g-4 px-3">
        {cards.map((item, index) => {
          const Icon = item.icon;
          return (
            <div className="col-12 col-sm-6 col-md-4 col-xl-3" key={index}>
              <div
                className="card h-100 border shadow-sm custom-hover-card"
                onClick={() => handleClick(item.plan)}
                style={{
                  background: "#ffffff",
                  cursor: "pointer",
                  borderRadius: "14px",
                  transition: "all 0.25s ease",
                  border: "1px solid var(--border-color)",
                }}
              >
                <div className="card-body p-4 d-flex justify-content-between align-items-start">
                  <div>
                    <h6
                      className="text-muted text-uppercase fw-semibold mb-2"
                      style={{ fontSize: "0.72rem", letterSpacing: "0.05em" }}
                    >
                      {item.title}
                    </h6>
                    <h3 className="fw-bold text-dark mb-0 fs-4">
                      {loading ? (
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                        ></span>
                      ) : (
                        salesData[item.key] || 0
                      )}
                    </h3>
                  </div>
                  <div
                    style={{
                      background: item.bg,
                      borderRadius: "12px",
                      padding: "0.6rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon size={20} color={item.color} />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        .custom-hover-card {
          transition: all 0.25s ease !important;
        }
        .custom-hover-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08) !important;
        }
      `}</style>
    </div>
  );
}
